import React, { useState } from "react";
import { MessageSquare, Flag, Ban, RotateCcw, Send, ChevronRight } from "lucide-react";
import { ccInput, ccLabel, ccCard, ccBtnPink, ccBtnBlue, ccBtnGhost, Avatar, EmptyState, StatusPill } from "./ui";
import LiveChat from "./LiveChat";

// ---------- My Requests ----------
export const RequestsScreen = ({ ctx }) => {
  const isCaregiver = ctx.viewUser.role === "caregiver";
  const myCgId = ctx.viewUser.caregiverId;
  const visible = ctx.requests.filter((r) =>
    ctx.viewUser.role === "admin" ? true : isCaregiver ? r.caregiverId === myCgId : r.familyKey === ctx.viewUser.key
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="cc-requests">
      <div>
        <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">
          {isCaregiver ? "Incoming Care Requests" : "My Care Requests"}
        </h2>
        <p className="text-xs text-[#9CA3AF] font-medium mt-1">
          Request Sent → Caregiver Responded → Connected → Arrangement Confirmed → Completed → Review
        </p>
      </div>

      {visible.length === 0 ? (
        <EmptyState text="No care requests yet. Find a caregiver and send your first request." testId="cc-requests-empty" />
      ) : (
        <div className="space-y-4">
          {visible.map((r) => {
            const cg = ctx.caregivers.find((c) => c.id === r.caregiverId);
            return (
              <div key={r.id} className={`${ccCard} p-5`} data-testid={`cc-request-item-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3.5">
                    <Avatar src={cg?.photo} name={isCaregiver ? r.familyName : cg?.name} />
                    <div>
                      <p className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100">
                        {isCaregiver ? `Family: ${r.familyName}` : cg?.name}
                      </p>
                      <p className="text-xs text-[#6B7280] dark:text-slate-400">
                        {r.service} · {r.date} · {r.startTime}–{r.endTime}
                      </p>
                      <p className="text-[11px] text-[#9CA3AF]">{r.location}</p>
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </div>
                {r.notes && (
                  <p className="text-xs text-[#6B7280] mt-3 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457] dark:text-slate-300">
                    “{r.notes}”
                  </p>                )}
                <div className="flex gap-2 mt-4 flex-wrap">
                  {isCaregiver && r.status === "sent" && (
                    <>
                      <button onClick={() => ctx.respondRequest(r.id, true)} className={ccBtnPink} data-testid={`cc-accept-${r.id}`}>
                        Accept
                      </button>
                      <button onClick={() => ctx.respondRequest(r.id, false)} className={ccBtnGhost} data-testid={`cc-decline-${r.id}`}>
                        Decline
                      </button>
                    </>
                  )}
                  {r.status === "connected" && (
                    <button onClick={() => ctx.advanceRequest(r.id, "confirmed")} className={ccBtnBlue} data-testid={`cc-confirm-${r.id}`}>
                      Confirm Arrangement
                    </button>
                  )}
                  {r.status === "confirmed" && (
                    <button onClick={() => ctx.advanceRequest(r.id, "completed")} className={ccBtnBlue} data-testid={`cc-complete-${r.id}`}>
                      Mark Completed
                    </button>
                  )}
                  {r.status === "completed" && (
                    <span className="text-xs font-bold text-[#7A5A17] self-center">Leave a review from My Caregiving → Reviews</span>
                  )}
                  <button onClick={() => ctx.openMessages(r.caregiverId)} className={ccBtnGhost} data-testid={`cc-request-message-${r.id}`}>
                    <MessageSquare className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    {isCaregiver ? "Message Family" : "Message"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------- My Connections ----------
export const ConnectionsScreen = ({ ctx }) => {
  const mine = ctx.connections.filter((c) =>
    ctx.viewUser.role === "admin" ? true : ctx.viewUser.role === "caregiver" ? c.caregiverId === ctx.viewUser.caregiverId : c.familyKey === ctx.viewUser.key
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="cc-connections">
      <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">
        {ctx.viewUser.role === "caregiver" ? "My Families" : "My Caregivers"}
      </h2>
      {mine.length === 0 ? (
        <EmptyState text="No connections yet. Accepted care requests will appear here." testId="cc-connections-empty" />
      ) : (
        <div className="space-y-4">
          {mine.map((con) => {
            const cg = ctx.caregivers.find((c) => c.id === con.caregiverId);
            if (!cg) return null;
            const isCaregiverView = ctx.viewUser.role === "caregiver";
            return (
              <div key={con.id} className={`${ccCard} p-5 flex items-center justify-between gap-3 flex-wrap`} data-testid={`cc-connection-${con.id}`}>
                <div className="flex items-center gap-3.5">
                  <Avatar src={isCaregiverView ? null : cg.photo} name={isCaregiverView ? con.familyName : cg.name} />
                  <div>
                    <p className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100">
                      {isCaregiverView ? con.familyName : cg.name}
                    </p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      Connected: {con.connectedDate}
                      {!isCaregiverView && cg.rating > 0 && ` · ★ ${cg.rating}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => ctx.openMessages(cg.id)} className={ccBtnGhost} data-testid={`cc-connection-message-${con.id}`}>
                    Message
                  </button>
                  <button onClick={() => ctx.openRequestModal(cg.id)} className={ccBtnPink} data-testid={`cc-request-again-${con.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Request Again
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------- Messages ----------
export const MessagesScreen = ({ ctx }) => {
  // Signed-in accounts get the real WebSocket chat; demo viewers keep the mock inbox
  if (ctx.currentUser?.real) return <LiveChat currentUser={ctx.currentUser} />;
  return <MockMessages ctx={ctx} />;
};

const MockMessages = ({ ctx }) => {
  const [draft, setDraft] = useState("");
  const threadIds = Object.keys(ctx.threads);
  const activeId = ctx.activeThreadId && threadIds.includes(ctx.activeThreadId) ? ctx.activeThreadId : threadIds[0];
  const activeCg = ctx.caregivers.find((c) => c.id === activeId);
  const messages = activeId ? ctx.threads[activeId] || [] : [];
  const isBlocked = ctx.blocked.includes(activeId);

  const send = (e) => {
    e.preventDefault();
    ctx.sendMessage(activeId, draft);
    setDraft("");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="cc-messages">
      {/* Thread list */}
      <div className={`${ccCard} p-4 md:col-span-1`}>
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-3 px-1">Messages</h3>
        {threadIds.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] p-2">No conversations yet.</p>
        ) : (
          <div className="space-y-1.5">
            {threadIds.map((tid) => {
              const cg = ctx.caregivers.find((c) => c.id === tid);
              const msgs = ctx.threads[tid] || [];
              const last = msgs[msgs.length - 1];
              return (
                <button
                  key={tid}
                  onClick={() => ctx.setActiveThreadId(tid)}
                  data-testid={`cc-thread-${tid}`}
                  className={`w-full p-3 rounded-2xl flex items-center gap-3 text-left transition-colors duration-200 ${
                    activeId === tid ? "bg-[#FCE6E9]" : "hover:bg-[#FFF9F2] dark:hover:bg-[#273244]"
                  }`}
                >
                  <Avatar src={cg?.photo} name={cg?.name} size="w-10 h-10" />
                  <div className="min-w-0">
                    <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100 truncate">{cg?.name || "Conversation"}</p>
                    <p className="text-[11px] text-[#9CA3AF] truncate">{last ? last.text : "Start the conversation"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Conversation */}
      <div className={`${ccCard} p-5 md:col-span-2 flex flex-col`} data-testid="cc-conversation">
        {activeCg ? (
          <>
            <div className="flex items-center justify-between border-b border-[#FCE6E9] dark:border-[#374457] pb-3 mb-4">
              <div className="flex items-center gap-3">
                <Avatar src={activeCg.photo} name={activeCg.name} size="w-9 h-9" />
                <p className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">{activeCg.name}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => ctx.reportThread(activeId)}
                  data-testid="cc-report-thread"
                  title="Report conversation"
                  className="w-8 h-8 flex items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#D9A93E] hover:bg-[#FFF2D1] transition-colors"
                >
                  <Flag className="w-4 h-4" />
                </button>
                <button
                  onClick={() => ctx.blockThread(activeId)}
                  data-testid="cc-block-thread"
                  title={isBlocked ? "Unblock user" : "Block user"}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                    isBlocked ? "text-rose-500 bg-rose-50" : "text-[#9CA3AF] hover:text-rose-400 hover:bg-rose-50"
                  }`}
                >
                  <Ban className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 space-y-3 max-h-96 overflow-y-auto pr-1 mb-4" data-testid="cc-message-list">
              {messages.length === 0 && <p className="text-xs text-[#9CA3AF] text-center py-6">Say hello to {activeCg.name}.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "me" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-3xl text-sm font-medium ${
                      m.sender === "me"
                        ? "bg-[#F8C8DC] text-[#374151] rounded-br-md"
                        : "bg-[#E6F2FA] text-[#374151] rounded-bl-md dark:text-slate-800"
                    }`}
                  >
                    <p>{m.text}</p>
                    <p className="text-[9px] opacity-60 mt-0.5 text-right">{m.time}</p>
                  </div>
                </div>
              ))}
            </div>

            {isBlocked ? (
              <p className="text-xs text-rose-500 font-bold text-center py-2" data-testid="cc-blocked-banner">
                You blocked this user. Unblock to resume messaging.
              </p>
            ) : (
              <form onSubmit={send} className="flex gap-2">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message..."
                  className={ccInput}
                  data-testid="cc-message-input"
                />
                <button type="submit" className={`${ccBtnPink} !px-4`} data-testid="cc-message-send">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </>
        ) : (
          <EmptyState text="Select a conversation, or message a caregiver from their profile." testId="cc-no-thread" />
        )}
      </div>
    </div>
  );
};
